import type {Header} from './Edf'

// The decoder (Edf.getData) and the renderers read signals 0..18 as the 19 EEG
// channels of the 10-20 montage.
export const DRAWN_CHANNELS = 19

const ANNOTATION_LABEL = /annotation/i

/**
 * Samples per SECOND of the EEG signals the viewer draws.
 *
 * EDF stores `numberOfSamples` per DATA RECORD, not per second: a 256 Hz signal
 * written in 2-second records has numberOfSamples = 512. The rate is therefore
 * numberOfSamples / recordDurationTime. The two coincide only for 1-second
 * records, which is why reading numberOfSamples as a rate looks right on every
 * file produced today and would be wrong on the first one that isn't.
 *
 * Throws instead of assuming 250 Hz: a guessed rate draws a time axis that is
 * plausible and wrong, and nobody notices. The caller decides how to show the
 * failure.
 */
export function getSamplingRate(header: Header): number {
  const first = header.channels?.[0]
  if (!first) {
    throw new Error('EDF header has no channels')
  }
  if (ANNOTATION_LABEL.test(first.label)) {
    throw new Error(
      `EDF signal 0 is "${
        first.label
      }", not an EEG channel: the viewer draws signals 0-${
        DRAWN_CHANNELS - 1
      } as EEG`,
    )
  }
  const samples = first.numberOfSamples
  if (!Number.isFinite(samples) || samples <= 0) {
    throw new Error(
      `EDF channel "${first.label}" has an invalid number of samples per record (${samples})`,
    )
  }
  const duration = header.recordDurationTime
  if (duration === null || !Number.isFinite(duration) || duration <= 0) {
    throw new Error(
      `EDF header has an invalid data record duration (${duration})`,
    )
  }
  return samples / duration
}

/**
 * Checks that the file matches what the EDF viewer can draw correctly, and says
 * why when it does not. Call it once after parsing, before rendering.
 *
 * - exactly 19 signals, none of them an annotation channel: the decoder draws
 *   signals 0..18 as EEG and advances through each data record by those 19
 *   only, so any extra signal would shift every record after the first;
 * - the same number of samples on all 19: the renderer walks every channel with
 *   the sample count of channel 0;
 * - 1-second data records: GraphV1 treats one data record as one second of the
 *   time axis.
 *
 * The EDFs the engine writes for the viewer satisfy all three (19 signals in
 * 10-20 order, 1-second records). A file that does not is refused rather than
 * drawn wrong.
 */
export function assertDrawableByViewer(header: Header): void {
  const channels = header.channels ?? []
  if (channels.length !== DRAWN_CHANNELS) {
    throw new Error(
      `EDF has ${channels.length} signals; the viewer reads exactly ${DRAWN_CHANNELS} per data record and would misalign every record after the first with any other count`,
    )
  }
  const drawn = channels.slice(0, DRAWN_CHANNELS)
  const annotation = drawn.find((c) => ANNOTATION_LABEL.test(c.label))
  if (annotation) {
    throw new Error(
      `EDF signal "${annotation.label}" is among the first ${DRAWN_CHANNELS}, which the viewer draws as EEG`,
    )
  }
  const rate = getSamplingRate(header)
  const samples = drawn[0].numberOfSamples
  const mixed = drawn.find((c) => c.numberOfSamples !== samples)
  if (mixed) {
    throw new Error(
      `EDF channels have different sample counts (${drawn[0].label}: ${samples}, ${mixed.label}: ${mixed.numberOfSamples}); mixed-rate files are not supported by the viewer`,
    )
  }
  if (header.recordDurationTime !== 1) {
    throw new Error(
      `EDF data records last ${header.recordDurationTime} s (${rate} Hz); the viewer draws one record as one second and needs 1-second records`,
    )
  }
}
